import { z } from 'zod/v4';
import {
  solarUsNetMeteringLedgerInputSchema,
  calculateSolarUsNetMeteringLedger,
} from './solarUsNetMeteringLedger';

/** Only an explicit opt-in overrides legacy flat-export calculations. */
export const solarUsNetMeteringPolicySchema =
  solarUsNetMeteringLedgerInputSchema
    .omit({
      generation: true,
      retailCentsPerKwh: true,
      compensation: true,
      costBasis: true,
    })
    .extend({
      schemaVersion: z.literal(1),
      mode: z.literal('monthlyKwhBank'),
      wholesaleCentsPerKwh: z.number().finite().nonnegative().max(1_000_000),
      settlementMonth: z.number().int().min(1).max(12),
      /**
       * Set by the server when the estimate is created (its payable total
       * includes installation lines); the designer never writes it.
       */
      installedCostConfirmed: z.boolean().optional(),
    })
    .strict();
export type SolarUsNetMeteringPolicy = z.infer<
  typeof solarUsNetMeteringPolicySchema
>;
export const solarUsNetMeteringWeatherProvenance =
  'PVWatts weather estimate: original same-source included-array January–December AC output, already including modelled losses and inverter conversion. No detailed site obstacle or row shading model; model defaults are not actual equipment AC ratings.';

export const solarUsNetMeteringInputFromPolicy = (
  policy: SolarUsNetMeteringPolicy,
  monthlyAcKwh: number[] | null,
  retailCentsPerKwh: number,
  costUsd?: number,
  generationProvenance = 'Verified original satellite AC and January–December monthly shares on the same source, panel plane, module and loss basis; not recalculated MCS output'
) => ({
  ...policy,
  generation:
    monthlyAcKwh === null
      ? null
      : {
          monthlyAcKwh,
          provenance: generationProvenance,
        },
  retailCentsPerKwh,
  compensation: {
    mode: 'monthlyKwhBank' as const,
    wholesaleCentsPerKwh: policy.wholesaleCentsPerKwh,
    settlementMonth: policy.settlementMonth,
  },
  costBasis:
    policy.installedCostConfirmed && costUsd !== undefined
      ? {
          kind: 'complete' as const,
          systemCostUsd: costUsd,
          description:
            'Installer confirmed complete installed pricing; estimate payable total at model capture',
        }
      : {
          kind: 'incomplete' as const,
          description:
            'Installed pricing not confirmed complete; no payback claim',
        },
});

/** Shared by designer and server: callers supply source generation, never results. */
export const calculateSolarUsNetMeteringPolicy = (
  policy: SolarUsNetMeteringPolicy,
  monthlyAcKwh: number[] | null,
  retailCentsPerKwh: number,
  costUsd?: number,
  generationProvenance?: string
) =>
  calculateSolarUsNetMeteringLedger(
    solarUsNetMeteringInputFromPolicy(
      policy,
      monthlyAcKwh,
      retailCentsPerKwh,
      costUsd,
      generationProvenance
    )
  );
